import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { GlobalToken } from 'antd';

import { EPInput, EPInputPassword, EulaPage } from '@/comp';
import { useStores } from '@/hooks/use-stores';
import { EPButton } from '@/components';
import EcoplantLogo from '@/comp/common/icons/EcoplantLogo';
import { AuthenticationType } from '@/store/SiteSurveyForm/constants';

const LoginPage = observer(() => {
  const { userStore } = useStores();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { from } = location.state || { from: { pathname: '/' } };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [errorText, setErrorText] = useState('');
  const [user, setUser] = useState();

  useEffect(() => {
    const errorMessage = userStore.passwordLoginFailureResponse;
    setErrorText(errorMessage);
  }, [userStore.passwordLoginFailureResponse]);

  useEffect(() => {
    if (userStore.hasSignedEula) {
      const redirectPath = localStorage.getItem('redirectAfterLogin') || from.pathname || '/home';
      localStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath, { replace: true });
    }
  }, [isSubmitting, userStore.hasSignedEula]);

  useEffect(() => {
    // redirect users who browse '/login/mfa' directly without actually have an open session
    if (!user && location.pathname === '/login/mfa') navigate('/login');
  }, []);

  const isFormLoginValid = () => !!email && !!password;

  const isFormMfaValid = () => !!mfaCode;

  const onChangeEmail = (e: { target: { value: React.SetStateAction<string> } }) => {
    setErrorText('');
    setEmail(e.target.value);
  };

  const onChangeMfaCode = e => {
    setErrorText('');
    setMfaCode(e.target.value);
  };

  const onChangePassword = e => {
    setErrorText('');
    setPassword(e.target.value);
  };

  const onSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      userStore.setAuthenticationType(AuthenticationType.PWD);
      const user = await userStore.signIn(email.trim(), password);

      switch (user.challengeName) {
        case 'SMS_MFA':
          setUser(user);
          navigate('/login/mfa');
          break;

        default:
          break;
      }
    } catch (err) {
      setErrorText(t('LoginFailurePasswordError', { error: err.message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitMfa = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!mfaCode) return;
    if (!user) return;

    try {
      await userStore.confirmSignIn({
        user,
        mfaCode,
        mfaType: 'SMS_MFA',
      });
    } catch (e) {
      setErrorText(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onClickResend = async () => {
    try {
      setIsResending(true);
      setErrorText('');
      const user = await userStore.signIn(email, password);
      setUser(user);
    } catch (e) {
      setErrorText(e.message);
    } finally {
      setIsResending(false);
    }
  };

  const onClickCancel = () => {
    setMfaCode(undefined);
    navigate('/login');
  };

  const onSSOLogin = () => {
    navigate('/login/sso');
  };

  const formLogin = (
    <form className={cn('form', { 'has-warning': errorText })} onSubmit={onSubmit} autoComplete="off">
      <EPInput
        placeholder={t('Email Address')}
        value={email}
        name="email"
        disabled={isSubmitting}
        onChange={onChangeEmail}
        className="email-input"
      />
      <EPInputPassword
        placeholder={t('Password')}
        name="password"
        disabled={isSubmitting}
        onChange={onChangePassword}
      />

      <div className="error-text">{errorText}</div>
      <div className="forgot-password">
        <a href={`${import.meta.env.VITE_FORGOT_PASSWORD_COGNITO}`}>{t('Forgot Password')}</a>
      </div>
      <EPButton
        loading={isSubmitting}
        type="primary"
        disabled={!isFormLoginValid() || isSubmitting}
        className="submit"
        htmlType="submit"
      >
        {t('Login')}
      </EPButton>
      <div className="line-container">
        <hr className="line" />
        <span>{t('OR')}</span>
        <hr className="line" />
      </div>
      <EPButton onClick={() => onSSOLogin()}>{t('Login with SSO')}</EPButton>
    </form>
  );

  const formMfa = (
    <form className={cn('form', { 'has-warning': errorText })} autoComplete="off" onSubmit={onSubmitMfa}>
      <p className="title-mfa">{t('SMS with a verification code was sent to your phone')}</p>

      <EPInput
        placeholder={t('Enter a verification code')}
        value={mfaCode}
        name="code"
        disabled={isSubmitting}
        onChange={onChangeMfaCode}
        className="mfa-code-input"
      />
      <div className="action-bar">
        <div className="action-item">
          <EPButton type="link" size="small" onClick={onClickCancel}>
            {t('Cancel')}
          </EPButton>
        </div>
        <div className="action-item">
          <EPButton type="link" size="small" loading={isResending} onClick={onClickResend}>
            {t('Send Again')}
          </EPButton>
        </div>
      </div>

      <div className="error-text">{errorText}</div>
      <EPButton
        loading={isSubmitting}
        type="primary"
        disabled={!isFormMfaValid() || isSubmitting}
        className="submit"
        htmlType="submit"
      >
        {t('Done')}
      </EPButton>
    </form>
  );

  return (
    <Container>
      {!userStore.hasUserLoggedIn ? (
        <div className="login-page">
          <div className="form-container">
            <div className="logo-container">
              <EcoplantLogo />
            </div>
            <p className="sign-in">{t('Sign in')}</p>

            <Routes>
              <Route path="/" element={formLogin} />
              <Route path="mfa" element={formMfa} />
            </Routes>

            <div className="email-us">
              {t('For technical support email us at:')}
              <a target="_blank" href="mailto:support@ecoplant.co" rel="noreferrer">
                {' '}
                support@ecoplant.co
              </a>
            </div>
          </div>
        </div>
      ) : (
        <EulaPage />
      )}
      <footer>
        <span className="right-reserved">© {t('EcoPlant 2020. All right reserved.')}</span>
      </footer>
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${({ theme: { token } }) => token.colorBgBase};

  .login-page {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;

    .title-mfa {
      max-width: 240px;
      margin: auto;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
    }

    .mfa-code-input {
      margin-top: ${({ theme: { token } }) => token.marginXL}px;
    }

    .form-container {
      text-align: center;
      width: 400px;

      .logo-container {
        display: flex;
        justify-content: center;
      }

      .sign-in {
        margin-top: ${({ theme: { token } }) => token.marginXXL}px;
        font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
        font-weight: normal;
      }

      .email-input {
        margin-bottom: ${({ theme: { token } }) => token.marginXL}px;
      }

      .form {
        margin-top: ${({ theme: { token } }) => token.marginLG}px;
        text-align: center;

        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-background-clip: text;
          -webkit-text-fill-color: ${({ theme: { token } }) => token.colorText};
          transition: background-color 5000s ease-in-out 0s;
          box-shadow: inset 0 0 20px 20px ${({ theme: { token } }) => token.colorBgContainer};
        }

        .action-bar {
          display: flex;
          justify-content: space-between;
          margin-top: ${({ theme: { token } }) => token.marginXS}px;
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        }

        .error-text {
          margin-top: ${({ theme: { token } }) => token.marginXS}px;
          font-size: ${({ theme: { token } }) => token.fontSize}px;
          color: ${({ theme: { token } }) => token.colorError};
        }

        .forgot-password {
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          color: ${({ theme: { token } }) => token.colorPrimary};
          margin-top: ${({ theme: { token } }) => token.marginLG}px;
          margin-bottom: ${({ theme: { token } }) => token.marginMD}px;
        }

        .line-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: ${({ theme: { token } }) => token.marginXXL}px 0;

          span {
            margin: 0 ${({ theme: { token } }) => token.margin}px;
            font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          }
        }

        .line {
          width: 100%;
          height: 0;
          margin: 0;
          border: solid 1px ${({ theme: { token } }) => token.colorBorder};
        }

        &.has-warning {
          > .ant-input,
          > .ant-input-affix-wrapper {
            border-color: ${({ theme: { token } }) => token.colorError};
          }
        }

        button.submit {
          margin-top: ${({ theme: { token } }) => token.marginMD}px;
        }
      }

      .email-us {
        margin-top: ${({ theme: { token } }) => token.marginXXL}px;
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ theme: { token } }) => token.colorTextDisabled};

        a {
          color: ${({ theme: { token } }) => token.colorPrimary};
        }
      }
    }
  }

  footer {
    text-align: center;
    margin-bottom: ${({ theme: { token } }) => token.marginXL}px;

    .right-reserved {
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      color: ${({ theme: { token } }) => token.colorTextDisabled};
    }
  }
`;

export default LoginPage;
